import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { useCopywriterAgent } from '../hooks/useCopywriterAgent';
import { Copy as CopyIcon, Link as LinkIcon } from 'lucide-react';
import { showToast } from '@/lib/toast';

export const CopywriterAgentPage: React.FC = () => {
  const {
    niche,
    setNiche,
    loading,
    result,
    generateContent,
    error,
    descInput,
    setDescInput
  } = useCopywriterAgent();

  // Função para gerar slug seguro
  const slugify = (str: string) =>
    str
      .normalize('NFD')
      .replace(/[^\w\s-]/g, '') // remove specials except space & -
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Copiar slug
  const handleCopySlug = async (title: string) => {
    const slug = slugify(title);
    try {
      await navigator.clipboard.writeText(slug);
      showToast('Slug copiado!');
    } catch {/* silêncio */}
  };

  // Copiar texto para área de transferência (mobile e desktop)
  const handleCopy = async (text: string | string[], label: string) => {
    try {
      let output: string;
      
      if (Array.isArray(text)) {
        // Para todos os arrays, copiar apenas o texto sem ícones
        output = text.join('\n');
      } else {
        output = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .join('\n');
      }

      await navigator.clipboard.writeText(output);
      showToast(`${label} copiado!`);
    } catch {
      /* silêncio */
    }
  };

  const isPlaceholder = (text: string | string[]) => {
    if (Array.isArray(text)) {
      return text.length === 0 || text.every(item => !item.trim());
    }
    return !text || !text.trim();
  };

  // Função para detectar tecla Enter e gerar conteúdo
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading && (niche.trim() || descInput.trim())) {
      e.preventDefault();
      generateContent();
    }
  };

  return (
    <div className="flex flex-col w-full h-full p-6 gap-8 items-center">
      <div className="text-center mb-2">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Agente CopyWriter</h1>
        <p className="text-sm text-muted-foreground max-w-xl mx-auto">
          Gere descrições profissionais e persuasivas para seus produtos em segundos
        </p>
      </div>

      <Separator className="max-w-6xl" />

      {/* Grid responsiva */}
      <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Coluna esquerda – Formulário */}
        <div>
          <div className="flex flex-col gap-4 bg-white dark:bg-slate-800/60 shadow-lg rounded-xl p-6">
            <Input
              placeholder="Título do produto (opcional)"
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              onKeyDown={handleKeyDown}
            />

            <textarea
              placeholder="Breve descrição do produto (opcional)"
              value={descInput}
              onChange={(e) => setDescInput(e.target.value)}
              className="w-full p-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[96px]"
              rows={4}
              onKeyDown={handleKeyDown}
            />

            {error && <p className="text-sm text-red-500">{error}</p>}

            <Button
              onClick={generateContent}
              disabled={loading || (!niche.trim() && !descInput.trim())}
              className="bg-emerald-600 text-white hover:bg-emerald-700 w-full md:w-auto md:self-start"
            >
              {loading ? 'Gerando...' : 'Gerar Conteúdo'}
            </Button>
          </div>
        </div>

        {/* Coluna direita – Resultado */}
        <div>
          {result ? (
            <ScrollArea className="h-[500px] md:h-[465px] w-full rounded-xl border p-4 bg-white/70 dark:bg-slate-800/50 shadow-inner">
              <div className="flex flex-col gap-6">
                {result.title && !isPlaceholder(result.title) && (
                <section>
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-xl font-semibold">Título Sugerido</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopy(result.title, 'Título')}
                        className="flex items-center gap-1 text-emerald-600 text-sm hover:text-emerald-700"
                      >
                        <CopyIcon className="w-4 h-4" /> Copiar
                      </button>
                      {/* Botão Copiar Slug - apenas mobile */}
                      <button
                        onClick={() => handleCopySlug(result.title)}
                        className="md:hidden flex items-center gap-1 text-emerald-600 text-sm hover:text-emerald-700"
                      >
                        <LinkIcon className="w-4 h-4" /> Slug
                      </button>
                    </div>
                  </div>
                  <p className="text-lg">{result.title}</p>
                </section>
                )}

                {result.features && result.features.length > 0 && !isPlaceholder(result.features) && (
                <section>
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-xl font-semibold">Características Principais</h2>
                    <button
                      onClick={() => handleCopy(result.features, 'Características')}
                      className="flex items-center gap-1 text-emerald-600 text-sm hover:text-emerald-700"
                    >
                      <CopyIcon className="w-4 h-4" /> Copiar
                    </button>
                  </div>
                  <ul className="space-y-1">
                    {result.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="mt-0.5">✅</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </section>
                )}

                {result.description && !isPlaceholder(result.description) && (
                <section>
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-xl font-semibold">Descrição Persuasiva</h2>
                    <button
                      onClick={() => handleCopy(result.description, 'Descrição')}
                      className="flex items-center gap-1 text-emerald-600 text-sm hover:text-emerald-700"
                    >
                      <CopyIcon className="w-4 h-4" /> Copiar
                    </button>
                  </div>
                  <p className="whitespace-pre-wrap">{result.description}</p>
                </section>
                )}

                {result.benefits && result.benefits.length > 0 && !isPlaceholder(result.benefits) && (
                <section>
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-xl font-semibold">Benefícios</h2>
                    <button
                      onClick={() => handleCopy(result.benefits, 'Benefícios')}
                      className="flex items-center gap-1 text-emerald-600 text-sm hover:text-emerald-700"
                    >
                      <CopyIcon className="w-4 h-4" /> Copiar
                    </button>
                  </div>
                  <ul className="space-y-1">
                    {result.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-emerald-600 font-semibold mt-0.5">✔</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </section>
                )}

                {result.triggers && result.triggers.length > 0 && !isPlaceholder(result.triggers) && (
                <section>
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-xl font-semibold">Gatilhos Mentais</h2>
                    <button
                      onClick={() => handleCopy(result.triggers, 'Gatilhos Mentais')}
                      className="flex items-center gap-1 text-emerald-600 text-sm hover:text-emerald-700"
                    >
                      <CopyIcon className="w-4 h-4" /> Copiar
                    </button>
                  </div>
                  <div>
                    {result.triggers.map((trigger, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className="mt-0.5">→</span>
                        <span>{trigger}</span>
                      </div>
                    ))}
                  </div>
                </section>
                )}
                
                {/* Fallback caso nenhuma seção seja exibida */}
                {(!result.title || isPlaceholder(result.title)) &&
                 (!result.features || result.features.length === 0 || isPlaceholder(result.features)) &&
                 (!result.description || isPlaceholder(result.description)) &&
                 (!result.benefits || result.benefits.length === 0 || isPlaceholder(result.benefits)) &&
                 (!result.triggers || result.triggers.length === 0 || isPlaceholder(result.triggers)) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Não foi possível gerar o conteúdo com os dados fornecidos.</p>
                    <p className="text-sm mt-2">Tente ser mais específico na descrição do produto.</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground border border-dashed rounded-xl p-6">
              <span className="text-sm text-center">O conteúdo gerado aparecerá aqui.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};